import axios from 'axios';
import Cookies from 'js-cookie';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

// ---- Types ---------------------------------------------------------------

export interface WhatsAppCloudStatus {
  configured: boolean;
  phone_number_id: string | null;
  waba_id: string | null;
  graph_version: string;
}

export interface WhatsAppCloudTestResult {
  ok: boolean;
  phone?: {
    id: string;
    display_phone_number?: string;
    verified_name?: string;
    quality_rating?: string;
    platform_type?: string;
    messaging_limit_tier?: string;
  };
  waba?: {
    id: string;
    name?: string;
    review_status?: string;
    namespace?: string;
  };
  graph_version?: string;
  error?: string;
  stage?: string;
}

export interface WhatsAppWebhookHealth {
  events_24h: number;
  failed_24h: number;
  last_event_at: string | null;
  seconds_since_last_event: number | null;
  subscribed_fields: string[];
}

export interface WhatsAppIntegrationError {
  id: string;
  kind: 'webhook' | 'send';
  code: string | null;
  message: string;
  ref: string | null;
  timestamp: string | null;
}

export interface WhatsAppTemplateButton {
  type: string;
  text?: string;
  url?: string;
  phone_number?: string;
}

export interface WhatsAppTemplateParam {
  index: number;
  /** Sample value — the CRM's edit if set, else Meta's approval example. */
  example?: string | null;
}

export interface WhatsAppCloudTemplate {
  id: number;
  provider_template_id: string;
  element_name: string;
  category: string | null;
  status: 'APPROVED' | 'PENDING' | 'REJECTED' | 'PAUSED' | string;
  /** false = only the system can send it (a value is generated at send time, e.g. a
   * Razorpay "Pay Now" link) -- hidden from manual pickers. */
  manual_send?: boolean;
  language: string;
  header: string | null;
  body: string;
  footer: string | null;
  buttons: WhatsAppTemplateButton[] | null;
  parameters: WhatsAppTemplateParam[] | null;
  last_synced_at: string | null;
  /** Meta's last_updated_time — best proxy for "date approved" (null for stale rows). */
  approved_at: string | null;
  /**
   * Send-time media source for a media-header template. Meta stores only an
   * approval sample, so without this the journey engine refuses to send.
   */
  header_media_url: string | null;
  /** Sample {{1}} value per dynamic URL button, keyed by button index. */
  button_examples?: Record<string, string>;
}

/** Header markers stored by the sync — a media header needs `header_media_url`. */
export const MEDIA_HEADER_MARKERS = ['[IMAGE]', '[VIDEO]', '[DOCUMENT]'];

export const isMediaHeader = (t: WhatsAppCloudTemplate): boolean =>
  MEDIA_HEADER_MARKERS.includes((t.header || '').trim().toUpperCase());

/** True when a media-header template still can't be sent by journeys. */
export const needsHeaderMedia = (t: WhatsAppCloudTemplate): boolean =>
  isMediaHeader(t) && !t.header_media_url;

/** IMAGE | VIDEO | DOCUMENT, derived from the stored header marker. */
export const headerFormatOf = (t: WhatsAppCloudTemplate): string =>
  (t.header || '').trim().toUpperCase().replace(/[[\]]/g, '');

/** Settings-page stats for one template (GET /templates/usage), keyed by element_name. */
export interface TemplateUsage {
  sent: number;
  /** delivered + read — status only moves forward. */
  delivered: number;
  read: number;
  failed: number;
  last_sent_at: string | null;
  /** Names of active journeys with a live step on this template. */
  journeys: string[];
}

export type HeaderFormat = 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
export type TemplateCategory = 'MARKETING' | 'UTILITY';
export type ButtonType = 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER';

export interface TemplateDraftButton {
  type: ButtonType;
  text: string;
  url?: string;
  /** Sample full URL, required when `url` contains {{1}}. */
  example?: string;
  phone_number?: string;
}

export interface TemplateDraft {
  name: string;
  language: string;
  category: TemplateCategory;
  header: {
    format: HeaderFormat;
    text?: string;
    example?: string;
    /** Meta approval sample — single-use, spend it on the next submit. */
    handle?: string;
    /** DO Spaces copy — the source Meta fetches on every send. */
    media_url?: string;
    filename?: string;
  };
  body: { text: string; examples: string[] };
  footer?: string;
  buttons: TemplateDraftButton[];
}

export interface CreateTemplateResult {
  ok: boolean;
  provider_template_id: string;
  element_name: string;
  status: string;
  category: string;
  language: string;
}

export interface MediaHandleResult {
  ok: boolean;
  handle: string;
  /** Public DO Spaces URL — persisted with the template and used on every send. */
  media_url: string;
  format: string;
  filename: string;
}

/** Languages Meta accepts, narrowed to the ones this WABA realistically uses. */
export const TEMPLATE_LANGUAGES: { code: string; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'en_US', label: 'English (US)' },
  { code: 'en_GB', label: 'English (UK)' },
  { code: 'hi', label: 'Hindi' },
  { code: 'te', label: 'Telugu' },
  { code: 'ta', label: 'Tamil' },
  { code: 'kn', label: 'Kannada' },
  { code: 'ml', label: 'Malayalam' },
  { code: 'mr', label: 'Marathi' },
  { code: 'gu', label: 'Gujarati' },
  { code: 'bn', label: 'Bengali' },
  { code: 'pa', label: 'Punjabi' },
];

/** Mirrors MEDIA_LIMITS in meta_whatsapp_service.py — keep the two in step. */
export const MEDIA_RULES: Record<string, { accept: string; maxMB: number }> = {
  IMAGE: { accept: 'image/jpeg,image/png', maxMB: 5 },
  VIDEO: { accept: 'video/mp4,video/3gpp', maxMB: 16 },
  DOCUMENT: { accept: 'application/pdf', maxMB: 100 },
};

interface ApiEnvelope<T> {
  success: boolean;
  message?: string;
  data: T;
  total?: number;
}

// ---- Service -------------------------------------------------------------

class WhatsAppCloudService {
  private base = `${API_BASE_URL}/whatsapp-cloud`;

  private getAuthHeaders() {
    let token = Cookies.get('crm_auth_token');
    if (!token && import.meta.env.VITE_BEARER_TOKEN) {
      token = import.meta.env.VITE_BEARER_TOKEN;
    }
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  async getStatus(): Promise<WhatsAppCloudStatus | null> {
    const res = await axios.get<ApiEnvelope<WhatsAppCloudStatus>>(
      `${this.base}/status`,
      { headers: this.getAuthHeaders() },
    );
    return res.data.success ? res.data.data : null;
  }

  /** Live connection test. Returns the result object even on failure (ok:false). */
  async testConnection(): Promise<WhatsAppCloudTestResult> {
    try {
      const res = await axios.post<ApiEnvelope<WhatsAppCloudTestResult>>(
        `${this.base}/test`,
        {},
        { headers: this.getAuthHeaders() },
      );
      return res.data.data ?? { ok: res.data.success };
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Connection failed';
      return { ok: false, error: msg };
    }
  }

  async getWebhookHealth(): Promise<WhatsAppWebhookHealth | null> {
    const res = await axios.get<ApiEnvelope<WhatsAppWebhookHealth>>(
      `${this.base}/webhook-health`,
      { headers: this.getAuthHeaders() },
    );
    return res.data.success ? res.data.data : null;
  }

  async getIntegrationErrors(): Promise<WhatsAppIntegrationError[]> {
    const res = await axios.get<ApiEnvelope<WhatsAppIntegrationError[]>>(
      `${this.base}/integration-errors`,
      { headers: this.getAuthHeaders() },
    );
    return res.data.success ? res.data.data || [] : [];
  }

  async getTemplateUsage(): Promise<Record<string, TemplateUsage>> {
    const res = await axios.get<ApiEnvelope<Record<string, TemplateUsage>>>(
      `${this.base}/templates/usage`,
      { headers: this.getAuthHeaders() },
    );
    return res.data.success ? res.data.data || {} : {};
  }

  /**
   * Save CRM-only sample values (no Meta call, no re-review). Blank entries fall
   * back to Meta's approval example.
   */
  async setSampleValues(
    templateId: number,
    samples: { body: Record<number, string>; buttons: Record<number, string> },
  ): Promise<void> {
    await axios.put(`${this.base}/templates/${templateId}/samples`, samples, {
      headers: this.getAuthHeaders(),
    });
  }

  /**
   * Hide a template from the CRM (soft delete — it stays on Meta). Rejects with the
   * API's message when an active journey still uses it.
   */
  async deleteTemplate(templateId: number): Promise<void> {
    await axios.delete(`${this.base}/templates/${templateId}`, {
      headers: this.getAuthHeaders(),
    });
  }

  async getTemplates(): Promise<WhatsAppCloudTemplate[]> {
    const res = await axios.get<ApiEnvelope<WhatsAppCloudTemplate[]>>(
      `${this.base}/templates`,
      { headers: this.getAuthHeaders() },
    );
    return res.data.success ? res.data.data || [] : [];
  }

  /**
   * Upload a header asset via Meta's resumable upload and get its handle back.
   * Handles are single-use and expire, so upload at pick-time and submit soon after.
   */
  async uploadHeaderMedia(file: File, format: HeaderFormat): Promise<MediaHandleResult> {
    const form = new FormData();
    form.append('file', file);
    form.append('format', format);
    const { Authorization } = this.getAuthHeaders();
    const res = await axios.post<ApiEnvelope<MediaHandleResult>>(
      `${this.base}/media-handle`,
      form,
      { headers: { Authorization } }, // let the browser set the multipart boundary
    );
    return res.data.data;
  }

  /**
   * Attach a send-time media source to an already-approved media-header template.
   * Pass a File to upload to Spaces, or a URL you already host. No Meta call.
   */
  async attachHeaderMedia(
    templateId: number, source: File | string,
  ): Promise<{ header_media_url: string }> {
    const form = new FormData();
    if (typeof source === 'string') form.append('media_url', source);
    else form.append('file', source);
    const { Authorization } = this.getAuthHeaders();
    const res = await axios.post<ApiEnvelope<{ header_media_url: string }>>(
      `${this.base}/templates/${templateId}/header-media`,
      form,
      { headers: { Authorization } }, // browser sets the multipart boundary
    );
    return res.data.data;
  }

  /**
   * Upload a header file for a single send (inbox) and get its public CDN URL.
   * Does not change the template's default header media.
   */
  async uploadHeaderForSend(file: File, format: string): Promise<string> {
    const form = new FormData();
    form.append('file', file);
    form.append('format', format);
    const { Authorization } = this.getAuthHeaders();
    const res = await axios.post<ApiEnvelope<{ url: string }>>(
      `${this.base}/header-upload`,
      form,
      { headers: { Authorization } }, // browser sets the multipart boundary
    );
    return res.data.data.url;
  }

  async clearHeaderMedia(templateId: number): Promise<void> {
    await axios.delete(`${this.base}/templates/${templateId}/header-media`, {
      headers: this.getAuthHeaders(),
    });
  }

  /** Submit a new template to Meta. Resolves with its initial (usually PENDING) status. */
  async createTemplate(draft: TemplateDraft): Promise<CreateTemplateResult> {
    const res = await axios.post<ApiEnvelope<CreateTemplateResult>>(
      `${this.base}/templates`,
      draft,
      { headers: this.getAuthHeaders() },
    );
    return res.data.data;
  }
}

export const whatsappCloudService = new WhatsAppCloudService();
export default whatsappCloudService;
